// quality-gate-exempt: pre-modularization
/**
 * PropBrowserModal Component
 * Modal for searching and adding props to the parlay builder
 */

import { OddsComparisonModal } from '@components/ui/OddsComparisonModal';
import { SPORTSBOOKS } from '@config/constants';
import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useGamesAndProps } from '@hooks/useGamesAndProps';
import { useOddsComparison } from '@hooks/useOddsComparison';
import { isTeamBet } from '@parlay-picker/shared/correlationUtils';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { leagueKeyToLeague, preferredLeagueKeyOrDefault } from '@parlay-picker/shared/league';
import type { BestOddsResult, BookOdds } from '@parlay-picker/shared/oddsUtils';
import {
  type ChoosableBookId,
  getBookDisplayName,
  isChoosableBookId,
  sportsbookForLeague,
} from '@parlay-picker/shared/sportsbook';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import type { BettableProp } from '@utils/propUtils';
import {
  formatPropType,
  getBetDisplayName,
  getBetDisplayProp,
  matchesPropType,
  parseInputText,
  propTypeToStatType,
} from '@utils/propUtils';
import { usePlayerIdMapVersion } from '@views/picks/utils';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BrowseTab } from './prop-browser/BrowseTab';
import { ImportTab } from './prop-browser/ImportTab';
import { PropBrowserFilters } from './prop-browser/PropBrowserFilters';
import { PropBrowserHeader } from './prop-browser/PropBrowserHeader';
import { matchParsedLegs, propToBuilderLeg } from './prop-browser/propBrowserHelpers';
import type {
  GameGroup,
  MatchResult,
  PropBrowserModalProps,
  TabType,
} from './prop-browser/propBrowserTypes';
import { usePropTypesBySection } from './prop-browser/usePropTypesBySection';

// =============================================================================
// Component
// =============================================================================

export function PropBrowserModal({
  isOpen,
  onClose,
  initialTab = 'browse',
  defaultLeague,
}: PropBrowserModalProps): ReactElement | null {
  // Subscribe to player ID map changes so headshots in .map() calls update
  usePlayerIdMapVersion();
  const { addLeg, isBlocked, getSportsbook, getLegCount, isLegInBuilder } = useParlayBuilderStore();
  const { user } = useAuth();
  const { showToast } = useToast();

  // League selection: prop from parent (PicksView's league), then user pref, then
  // the season-aware default (not a hardcoded NBA, which is off-season half the
  // year and opens the browser on an empty "No games available today").
  const defaultLeagueKey =
    defaultLeague ?? preferredLeagueKeyOrDefault(user?.preferences?.preferredLeague);
  const [selectedLeagueKey, setSelectedLeagueKey] = useState<LeagueKey>(defaultLeagueKey);
  const leagueKey = selectedLeagueKey;
  const selectedLeague = leagueKeyToLeague(leagueKey);

  // Reset league when modal opens with a new default
  useEffect(() => {
    if (isOpen && defaultLeague) {
      setSelectedLeagueKey(defaultLeague);
    }
  }, [isOpen, defaultLeague]);

  // Reset game/search filters when league changes (games are different per league)
  const prevLeagueRef = useRef(selectedLeagueKey);
  // A book picked here; cleared on a league switch so each league opens at the user's book for it.
  const [pickedSportsbook, setPickedSportsbook] = useState<ChoosableBookId | null>(null);
  useEffect(() => {
    if (prevLeagueRef.current !== selectedLeagueKey) {
      prevLeagueRef.current = selectedLeagueKey;
      setGameFilter('all');
      setSearch('');
      setPropTypeFilter('all');
      setPickedSportsbook(null);
    }
  }, [selectedLeagueKey]);

  // Sportsbook selection: locked to builder's book if legs exist, otherwise switchable
  const builderSportsbook = getSportsbook();
  const isSportsbookLocked = builderSportsbook !== null;
  const selectedSportsbook: ChoosableBookId =
    isSportsbookLocked && isChoosableBookId(builderSportsbook)
      ? builderSportsbook
      : (pickedSportsbook ?? sportsbookForLeague(user?.preferences, selectedLeagueKey));
  const sportsbookName = getBookDisplayName(builderSportsbook ?? selectedSportsbook);

  // Use hook for games and props fetching (filtered by selected league)
  const {
    games,
    props: availableProps,
    loading,
    error,
    fetchGamesAndProps: hookFetchGamesAndProps,
  } = useGamesAndProps({ league: selectedLeague });

  // Feature access — hide line shopping for free users or when user disabled it
  const { isLocked } = useFeatureAccess();
  const oddsLocked =
    isLocked('odds_comparison') || user?.preferences?.enableLineComparison === false;

  // Multi-book odds comparison for Best Line Finder badges
  const { comparisons: oddsComparisons, fetchComparison } = useOddsComparison();
  const [comparisonModalData, setComparisonModalData] = useState<BestOddsResult | null>(null);
  const [comparisonSourceProp, setComparisonSourceProp] = useState<BettableProp | null>(null);

  // Tabs - reset to initialTab when modal opens
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  // Reset tab when modal opens with different initialTab
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Import state
  const [importText, setImportText] = useState('');
  const [matchResults, setMatchResults] = useState<MatchResult[] | null>(null);
  const [addedPropIds, setAddedPropIds] = useState<Set<string>>(new Set());
  // User overrides for match results (index -> selected prop)
  const [overrides, setOverrides] = useState<Map<number, BettableProp>>(new Map());

  // Filters
  const [search, setSearch] = useState('');
  const [gameFilter, setGameFilter] = useState('all');
  const [propTypeFilter, setPropTypeFilter] = useState('all');
  const [showAlts, setShowAlts] = useState(false);

  const fetchGamesAndProps = useCallback(async (): Promise<void> => {
    try {
      await hookFetchGamesAndProps(selectedSportsbook);
    } catch (_err) {
      // Error already handled by hook
    }
  }, [hookFetchGamesAndProps, selectedSportsbook]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  // Fetch games and props when modal opens, sportsbook changes, or league changes
  useEffect(() => {
    if (isOpen) {
      fetchGamesAndProps();
    }
  }, [isOpen, fetchGamesAndProps]);

  // Fetch multi-book comparison data when games are available (Pro+ only)
  useEffect(() => {
    if (oddsLocked) return;
    if (isOpen && games.length > 0) {
      const gameIds = games.map((g) => g.id);
      fetchComparison(gameIds, selectedSportsbook);
    }
  }, [isOpen, games, selectedSportsbook, fetchComparison, oddsLocked]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // Grouped prop-type sections for the filter dropdown
  const propTypesBySection = usePropTypesBySection(availableProps);

  // Filter props
  const filteredProps = useMemo(() => {
    const isAltProp = (pt?: string) => pt?.endsWith('_alternate') || pt?.startsWith('alternate_');
    const stripAltSuffix = (pt?: string) =>
      pt?.replace(/_alternate$/, '').replace(/^alternate_/, '') ?? pt;

    return availableProps.filter((prop) => {
      // Filter out alternate lines unless toggled on
      if (!showAlts && isAltProp(prop.propType)) return false;

      // Filter by game
      const matchesGame = gameFilter === 'all' || prop.gameId === gameFilter;

      // Filter by prop type (strip alt prefix/suffix for matching base type)
      const matchesPropTypeFilter =
        propTypeFilter === 'all' || stripAltSuffix(prop.propType) === propTypeFilter;

      // Filter by search
      if (!search) return matchesGame && matchesPropTypeFilter;

      const searchLower = search.toLowerCase();
      const matchesSearch =
        prop.playerName?.toLowerCase().includes(searchLower) ||
        prop.team?.toLowerCase().includes(searchLower) ||
        matchesPropType(prop.propType, searchLower);

      return matchesSearch && matchesGame && matchesPropTypeFilter;
    });
  }, [availableProps, gameFilter, propTypeFilter, search, showAlts]);

  // Group props by game -> player for browse view
  const groupedProps = useMemo((): GameGroup[] | null => {
    const byGame = new Map<string, BettableProp[]>();
    for (const prop of filteredProps) {
      const key = prop.gameId || 'unknown';
      if (!byGame.has(key)) byGame.set(key, []);
      byGame.get(key)!.push(prop);
    }

    return games
      .filter((g) => byGame.has(g.id))
      .sort((a, b) => new Date(a.commence_time).getTime() - new Date(b.commence_time).getTime())
      .map((game) => {
        const gameAllProps = byGame.get(game.id) || [];
        const gameProps = gameAllProps.filter((p) => isTeamBet(p.propType));
        const playerProps = gameAllProps.filter((p) => !isTeamBet(p.propType));

        const byPlayer = new Map<string, BettableProp[]>();
        for (const p of playerProps) {
          const name = p.playerName || 'Unknown';
          if (!byPlayer.has(name)) byPlayer.set(name, []);
          byPlayer.get(name)!.push(p);
        }

        const playerGroups = Array.from(byPlayer.entries()).map(([name, props]) => ({
          playerName: name,
          team: props[0]?.team || '',
          props: props.sort((a, b) =>
            formatPropType(a.propType).localeCompare(formatPropType(b.propType)),
          ),
        }));

        return { game, playerGroups, gameProps };
      });
  }, [filteredProps, games]);

  // Collapsed game sections — default all collapsed except first
  const [collapsedGames, setCollapsedGames] = useState<Set<string>>(new Set());
  const hasSetInitialCollapse = useRef(false);

  // Default-collapse all games except the first when grouped data first loads.
  // This reduces DOM from ~6800 prop pills to ~680, keeping interactions snappy.
  useEffect(() => {
    if (groupedProps && groupedProps.length > 1 && !hasSetInitialCollapse.current) {
      hasSetInitialCollapse.current = true;
      setCollapsedGames(new Set(groupedProps.slice(1).map((g) => g.game.id)));
    }
  }, [groupedProps]);

  // Auto-expand game when selected via game filter chip
  useEffect(() => {
    if (gameFilter !== 'all') {
      setCollapsedGames((prev) => {
        if (!prev.has(gameFilter)) return prev;
        const next = new Set(prev);
        next.delete(gameFilter);
        return next;
      });
    }
  }, [gameFilter]);

  const toggleGameCollapsed = useCallback((gameId: string) => {
    setCollapsedGames((prev) => {
      const next = new Set(prev);
      if (next.has(gameId)) {
        next.delete(gameId);
      } else {
        next.add(gameId);
      }
      return next;
    });
  }, []);

  // Player-level collapse within expanded games — reduces DOM from ~500 to ~60 pills
  const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(new Set());
  const initializedGames = useRef(new Set<string>());

  // Auto-expand first 4 players when a game first becomes visible
  useEffect(() => {
    if (!groupedProps) return;
    const updates: string[] = [];
    for (const group of groupedProps) {
      if (collapsedGames.has(group.game.id)) continue;
      if (initializedGames.current.has(group.game.id)) continue;
      initializedGames.current.add(group.game.id);
      for (const pg of group.playerGroups.slice(0, 4)) {
        updates.push(`${group.game.id}:${pg.playerName}`);
      }
    }
    if (updates.length > 0) {
      setExpandedPlayers((prev) => {
        const next = new Set(prev);
        for (const k of updates) next.add(k);
        return next;
      });
    }
  }, [groupedProps, collapsedGames]);

  const togglePlayer = useCallback((playerKey: string) => {
    setExpandedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(playerKey)) {
        next.delete(playerKey);
      } else {
        next.add(playerKey);
      }
      return next;
    });
  }, []);

  // Check if a prop would be blocked (stable ref to avoid pill re-renders)
  const checkBlocked = useCallback(
    (prop: BettableProp): { canAdd: boolean; reason?: string } => {
      const blockCheck = isBlocked({
        player: prop.playerName,
        team: prop.team,
        statType: propTypeToStatType(prop.propType),
        overUnder: prop.overUnder as 'Over' | 'Under' | null,
        line: typeof prop.line === 'number' ? prop.line : null,
        gameId: prop.gameId,
        sportsbook: selectedSportsbook,
      });
      return { canAdd: blockCheck.canAdd, reason: blockCheck.blockReason };
    },
    [isBlocked, selectedSportsbook],
  );

  // Handle adding a prop (stable ref to avoid pill re-renders)
  const handleAddProp = useCallback(
    (prop: BettableProp): void => {
      const builderLeg = propToBuilderLeg(prop, selectedSportsbook, games, leagueKey);
      const result = addLeg(builderLeg);
      if (!result.success && result.error) {
        showToast(result.error, 'error');
      } else if (result.success) {
        const display = getBetDisplayName(prop);
        const propDisplay = getBetDisplayProp(prop);
        showToast(`Added ${display} ${propDisplay}`, 'success');
      }
    },
    [selectedSportsbook, games, leagueKey, addLeg, showToast],
  );

  // Handle adding a prop from the odds comparison modal (specific sportsbook)
  const handleAddFromBook = useCallback(
    (book: BookOdds): void => {
      if (!comparisonSourceProp) return;

      const builderLeg = propToBuilderLeg(comparisonSourceProp, book.sportsbook, games, leagueKey);
      builderLeg.odds = book.odds;

      const result = addLeg(builderLeg);
      if (!result.success && result.error) {
        showToast(result.error, 'error');
      } else if (result.success) {
        const display = getBetDisplayName(comparisonSourceProp);
        const propDisplay = getBetDisplayProp(comparisonSourceProp);
        const bookName = SPORTSBOOKS[book.sportsbook]?.name || book.sportsbook;
        showToast(`Added ${display} ${propDisplay} from ${bookName}`, 'success');
        setComparisonModalData(null);
        setComparisonSourceProp(null);
      }
    },
    [comparisonSourceProp, games, leagueKey, addLeg, showToast],
  );

  // Handle parsing import text
  const handleParse = (): void => {
    if (!importText.trim()) {
      setMatchResults(null);
      return;
    }
    const parsed = parseInputText(importText);
    if (parsed.length === 0) {
      showToast('Could not parse any bets from the text', 'error');
      return;
    }
    const results = matchParsedLegs(parsed, availableProps, games);
    setMatchResults(results);
    setAddedPropIds(new Set());
    setOverrides(new Map()); // Clear any previous overrides
  };

  // Handle selecting a different prop for a match result
  const handleSelectOverride = (index: number, prop: BettableProp): void => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(index, prop);
      return next;
    });
  };

  // Get the effective prop for a match result (override or original match)
  const getEffectiveProp = (index: number, result: MatchResult): BettableProp | null => {
    return overrides.get(index) || result.matched;
  };

  // Handle adding from import results
  const handleAddFromImport = (prop: BettableProp): void => {
    handleAddProp(prop);
    setAddedPropIds((prev) => new Set([...prev, prop.id]));
  };

  // Handle adding all matched props
  const handleAddAllMatched = (): void => {
    if (!matchResults) return;

    matchResults.forEach((result, index) => {
      const effectiveProp = getEffectiveProp(index, result);
      if (effectiveProp && !addedPropIds.has(effectiveProp.id)) {
        const blocked = checkBlocked(effectiveProp);
        if (blocked.canAdd) {
          handleAddProp(effectiveProp);
          setAddedPropIds((prev) => new Set([...prev, effectiveProp.id]));
        }
      }
    });
  };

  // Count matched and addable props (using effective props with overrides)
  const matchedCount = matchResults?.filter((r, i) => getEffectiveProp(i, r) !== null).length ?? 0;
  const addableCount =
    matchResults?.filter((r, i) => {
      const prop = getEffectiveProp(i, r);
      return prop && !addedPropIds.has(prop.id) && checkBlocked(prop).canAdd;
    }).length ?? 0;

  const legCount = getLegCount();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
        onTouchMove={(e) => e.preventDefault()}
      />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col mx-4 bg-theme-surface/95 backdrop-blur-sm rounded-2xl border border-theme-border shadow-2xl shadow-black/40">
        {/* Header */}
        <PropBrowserHeader
          isSportsbookLocked={isSportsbookLocked}
          sportsbookName={sportsbookName}
          selectedSportsbook={selectedSportsbook}
          onSportsbookChange={setPickedSportsbook}
          leagueKey={selectedLeagueKey}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onClose={onClose}
        />

        {/* Filters - only show for Browse tab */}
        {activeTab === 'browse' && (
          <PropBrowserFilters
            selectedLeagueKey={selectedLeagueKey}
            onLeagueChange={(lk) => setSelectedLeagueKey(lk)}
            search={search}
            onSearchChange={setSearch}
            propTypeFilter={propTypeFilter}
            onPropTypeFilterChange={setPropTypeFilter}
            propTypesBySection={propTypesBySection}
            showAlts={showAlts}
            onShowAltsToggle={() => setShowAlts((prev) => !prev)}
            games={games}
            gameFilter={gameFilter}
            onGameFilterChange={(id) => setGameFilter(id)}
          />
        )}

        {/* Content — both tabs stay mounted; inactive one is hidden to avoid remount lag */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4">
          {/* Browse Tab */}
          <div className={activeTab !== 'browse' ? 'hidden' : ''}>
            <BrowseTab
              leagueKey={leagueKey}
              loading={loading}
              error={error}
              games={games}
              availableProps={availableProps}
              filteredProps={filteredProps}
              groupedProps={groupedProps}
              collapsedGames={collapsedGames}
              toggleGameCollapsed={toggleGameCollapsed}
              expandedPlayers={expandedPlayers}
              togglePlayer={togglePlayer}
              checkBlocked={checkBlocked}
              handleAddProp={handleAddProp}
              fetchGamesAndProps={fetchGamesAndProps}
              oddsComparisons={oddsComparisons}
              oddsLocked={oddsLocked}
              setComparisonModalData={setComparisonModalData}
              setComparisonSourceProp={setComparisonSourceProp}
            />
          </div>

          {/* Import Tab */}
          <div className={activeTab !== 'import' ? 'hidden' : ''}>
            <ImportTab
              loading={loading}
              availableProps={availableProps}
              importText={importText}
              setImportText={setImportText}
              matchResults={matchResults}
              addedPropIds={addedPropIds}
              overrides={overrides}
              handleParse={handleParse}
              handleAddAllMatched={handleAddAllMatched}
              handleAddFromImport={handleAddFromImport}
              handleSelectOverride={handleSelectOverride}
              getEffectiveProp={getEffectiveProp}
              checkBlocked={checkBlocked}
              matchedCount={matchedCount}
              addableCount={addableCount}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-theme-border/50 bg-theme-bg/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {legCount > 0 && (
                <span className="text-xs font-medium bg-theme-primary/20 text-theme-primary px-2 py-1 rounded-full">
                  {legCount} leg{legCount !== 1 ? 's' : ''}
                </span>
              )}
              <span className="text-sm text-theme-text-tertiary">
                {activeTab === 'browse' && `${filteredProps.length} props available`}
                {activeTab === 'import' &&
                  matchResults &&
                  `${matchedCount} matched, ${addableCount} can be added`}
                {activeTab === 'import' && !matchResults && 'Paste bets to import'}
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-white transition bg-theme-surface hover:bg-theme-surface-hover rounded-xl"
            >
              Done
            </button>
          </div>
        </div>
      </div>

      {/* Odds comparison modal (all books for a prop) */}
      {!oddsLocked && comparisonModalData && (
        <OddsComparisonModal
          comparison={comparisonModalData}
          onClose={() => {
            setComparisonModalData(null);
            setComparisonSourceProp(null);
          }}
          onAddFromBook={handleAddFromBook}
          builderSportsbook={getSportsbook()}
          isInBuilder={
            comparisonSourceProp
              ? isLegInBuilder(
                  comparisonSourceProp.playerName ?? null,
                  propTypeToStatType(comparisonSourceProp.propType),
                  typeof comparisonSourceProp.line === 'number' ? comparisonSourceProp.line : null,
                )
              : false
          }
        />
      )}
    </div>
  );
}
